import { createPortal } from "react-dom";
import { useRef, useState } from "react";
import type { Model } from "@lumen/shared";

export function ModelTooltipRow({
  model,
  details,
  children,
}: {
  model: Model;
  details: string[];
  children: React.ReactNode;
}) {
  const [position, setPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const row = useRef<HTMLDivElement>(null);
  const tooltipId = `model-info-${model.id}`;
  const show = () => {
    if (window.matchMedia("(max-width: 639px)").matches) return;
    const bounds = row.current?.getBoundingClientRect();
    if (!bounds) return;
    const left =
      bounds.right + 8 + 288 <= window.innerWidth
        ? bounds.right + 8
        : Math.max(8, bounds.left - 296);
    setPosition({
      left,
      top: Math.max(8, Math.min(bounds.top, window.innerHeight - 188)),
    });
  };
  return (
    <div
      ref={row}
      aria-describedby={position ? tooltipId : undefined}
      onMouseEnter={show}
      onMouseLeave={() => setPosition(null)}
      onFocusCapture={show}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setPosition(null);
      }}
      className="flex items-stretch rounded-xl hover:bg-stone-100 dark:hover:bg-white/5"
    >
      {children}
      {position &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={position}
            className="pointer-events-none fixed z-[100] w-72 rounded-xl border border-stone-200 bg-white p-3 text-left text-[11px] leading-4 text-stone-600 shadow-xl dark:border-white/10 dark:bg-stone-800 dark:text-stone-200"
          >
            <span className="mb-1 block text-xs font-semibold text-stone-900 dark:text-white">
              {model.name}
            </span>
            {details.map((detail) => (
              <span key={detail} className="block break-words">
                {detail}
              </span>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
